import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import supportTicketService from '../../services/supportTicketService';
import QuickAccessDashboard from './components/QuickAccessDashboard';
import TicketSubmissionWizard from './components/TicketSubmissionWizard';
import TicketTrackingPanel from './components/TicketTrackingPanel';
import KnowledgeBasePanel from './components/KnowledgeBasePanel';

const ClientSupportPortalInterface = () => {
  const [tickets, setTickets] = useState([]);
  const [knowledgeBase, setKnowledgeBase] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showSubmitWizard, setShowSubmitWizard] = useState(false);
  const [filters, setFilters] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [ticketsData, kbData] = await Promise.all([
        supportTicketService?.getAllTickets(),
        supportTicketService?.getKnowledgeBaseArticles()
      ]);

      setTickets(ticketsData);
      setKnowledgeBase(kbData);
    } catch (err) {
      setError(err?.message || 'Failed to load support portal data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleSubmitTicket = async (ticketData) => {
    try {
      await supportTicketService?.createTicket(ticketData);
      setShowSubmitWizard(false);
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to submit ticket');
    }
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: 'LayoutGrid' },
    { id: 'tickets', label: 'My Tickets', icon: 'Inbox', count: tickets?.length },
    { id: 'knowledge', label: 'Knowledge Base', icon: 'BookOpen', count: knowledgeBase?.length },
    { id: 'submit', label: 'Submit Ticket', icon: 'Plus' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ComplianceAlertBanner />
      <GlobalFilterBar onFilterChange={handleFilterChange} />

      <main className="container mx-auto px-4 py-6">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Client Support Portal</h1>
              <p className="text-muted-foreground mt-1">
                Self-service support portal for submitting requests and tracking resolutions
              </p>
            </div>
            <div className="flex items-center gap-3">
              <DataRefreshIndicator onRefresh={loadData} />
              <Button onClick={() => setShowSubmitWizard(true)}>
                <Icon name="Plus" size={16} className="mr-2" />
                New Ticket
              </Button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <Icon name="AlertCircle" size={20} className="text-destructive" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-border mb-6">
          <div className="flex gap-6">
            {tabs?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => {
                  if (tab?.id === 'submit') {
                    setShowSubmitWizard(true);
                  } else {
                    setActiveTab(tab?.id);
                  }
                }}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary font-medium' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={18} />
                {tab?.label}
                {tab?.count !== undefined && (
                  <span className="bg-muted px-2 py-0.5 rounded-full text-xs">
                    {tab?.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-card border border-border rounded-lg">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Icon name="Loader2" size={32} className="animate-spin text-primary mx-auto mb-3" />
                <p className="text-muted-foreground">Loading support portal...</p>
              </div>
            </div>
          ) : (
            <>
              {activeTab === 'dashboard' && (
                <QuickAccessDashboard tickets={tickets} knowledgeBase={knowledgeBase} />
              )}
              {activeTab === 'tickets' && (
                <TicketTrackingPanel tickets={tickets} onRefresh={loadData} />
              )}
              {activeTab === 'knowledge' && (
                <KnowledgeBasePanel articles={knowledgeBase} />
              )}
            </>
          )}
        </div>
      </main>

      {/* Ticket Submission Wizard */}
      {showSubmitWizard && (
        <TicketSubmissionWizard
          onClose={() => setShowSubmitWizard(false)}
          onSubmit={handleSubmitTicket}
        />
      )}
    </div>
  );
};

export default ClientSupportPortalInterface;